import React, { useState, useEffect } from 'react';
import { adminPromotionAPI, adminCategoryAPI, adminProductAPI } from '../services/api';
import '../styles/Modal.css';

const PromotionModal = ({ isOpen, onClose, onSuccess, promotion = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    bannerImage: '',
    categoryTitle: '',
    categoryImageUrl: '',
    startDate: '',
    endDate: '',
    discountType: 'percentage',
    discountValue: '',
    termsAndConditions: [''],
    applicableCategories: [],
    featuredProducts: [],
    status: 'Inactive',
    priority: 0,
    promoCode: '',
    maxUsage: '',
    seoTitle: '',
    seoDescription: ''
  });

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [productSearchQuery, setProductSearchQuery] = useState('');

  // Filter products based on selected applicable categories and search query
  const filteredProducts = React.useMemo(() => {
    let filtered = products;

    // Filter by selected categories
    if (formData.applicableCategories && formData.applicableCategories.length > 0) {
      // Get the titles of selected categories
      const selectedCategoryTitles = formData.applicableCategories.map(cat => cat.title);

      // Filter products where productCategory matches any selected category title
      filtered = products.filter(product => {
        return selectedCategoryTitles.some(categoryTitle =>
          product.productCategory === categoryTitle
        );
      });
    }

    // Filter by search query
    if (productSearchQuery.trim()) {
      const query = productSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(product => {
        const productName = (product.productName || '').toLowerCase();
        const catalogNumber = (product.catalogNumber || '').toLowerCase();
        return productName.includes(query) || catalogNumber.includes(query);
      });
    }

    return filtered;
  }, [products, formData.applicableCategories, productSearchQuery]);

  useEffect(() => {
    if (isOpen) {
      fetchCategoriesAndProducts();
      if (promotion) {
        setFormData({
          title: promotion.title || '',
          description: promotion.description || '',
          bannerImage: promotion.bannerImage || '',
          categoryTitle: promotion.categoryTitle || '',
          categoryImageUrl: promotion.categoryImageUrl || '',
          startDate: promotion.startDate ? new Date(promotion.startDate).toISOString().slice(0, 16) : '',
          endDate: promotion.endDate ? new Date(promotion.endDate).toISOString().slice(0, 16) : '',
          discountType: promotion.discountType || 'percentage',
          discountValue: promotion.discountValue || '',
          termsAndConditions: promotion.termsAndConditions?.length > 0 ? promotion.termsAndConditions : [''],
          applicableCategories: promotion.applicableCategories || [],
          featuredProducts: promotion.featuredProducts?.map(prod => prod._id || prod) || [],
          status: promotion.status || 'Inactive',
          priority: promotion.priority || 0,
          promoCode: promotion.promoCode || '',
          maxUsage: promotion.maxUsage || '',
          seoTitle: promotion.seoTitle || '',
          seoDescription: promotion.seoDescription || ''
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, promotion]);

  const fetchCategoriesAndProducts = async () => {
    try {
      const [categoriesRes, productsRes] = await Promise.all([
        adminCategoryAPI.getAll({ limit: 100, status: 'Active' }),
        adminProductAPI.getAll({ limit: 100, status: 'Active' })
      ]);
      console.log("categoriesRes", categoriesRes);
      console.log("productsRes", productsRes);
      setCategories(
        Array.isArray(categoriesRes.data.data.categories)
          ? categoriesRes.data.data.categories
          : []
      );
      setProducts(Array.isArray(productsRes.data.data) ? productsRes.data.data : []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setCategories([]);
      setProducts([]);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      bannerImage: '',
      categoryTitle: '',
      categoryImageUrl: '',
      startDate: '',
      endDate: '',
      discountType: 'percentage',
      discountValue: '',
      termsAndConditions: [''],
      applicableCategories: [],
      featuredProducts: [],
      status: 'Inactive',
      priority: 0,
      promoCode: '',
      maxUsage: ''
    });
    setError('');
    setFieldErrors({});
    setProductSearchQuery('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTermsChange = (index, value) => {
    const newTerms = [...formData.termsAndConditions];
    newTerms[index] = value;
    setFormData(prev => ({
      ...prev,
      termsAndConditions: newTerms
    }));
  };

  const addTerm = () => {
    setFormData(prev => ({
      ...prev,
      termsAndConditions: [...prev.termsAndConditions, '']
    }));
  };

  const removeTerm = (index) => {
    setFormData(prev => ({
      ...prev,
      termsAndConditions: prev.termsAndConditions.filter((_, i) => i !== index)
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => {
      // Check if category is already selected
      const isRemoving = prev.applicableCategories.some(cat => cat.id === categoryId);

      let newCategories;
      if (isRemoving) {
        // Remove the category object
        newCategories = prev.applicableCategories.filter(cat => cat.id !== categoryId);
      } else {
        // Add new category object with id, title, and imageUrl
        const category = categories.find(cat => cat._id === categoryId);
        if (category) {
          newCategories = [
            ...prev.applicableCategories,
            {
              id: category._id,
              title: category.title || '',
              imageUrl: category.imageUrl || ''
            }
          ];
        } else {
          newCategories = prev.applicableCategories;
        }
      }

      // If removing a category, also remove products from that category
      let newFeaturedProducts = prev.featuredProducts;
      let newCategoryTitle = prev.categoryTitle;
      let newCategoryImageUrl = prev.categoryImageUrl;

      if (isRemoving) {
        // Get the category being removed
        const removedCategory = prev.applicableCategories.find(cat => cat.id === categoryId);
        if (removedCategory) {
          // Filter out products that belong to the removed category
          newFeaturedProducts = prev.featuredProducts.filter(productId => {
            const product = products.find(p => p._id === productId);
            return product && product.productCategory !== removedCategory.title;
          });
        }
      }

      // Update categoryTitle and categoryImageUrl based on selected categories
      if (newCategories.length > 0) {
        // Get the first selected category to set title and image
        const firstCategory = newCategories[0];
        newCategoryTitle = firstCategory.title || '';
        newCategoryImageUrl = firstCategory.imageUrl || '';
      } else {
        // Clear if no categories selected
        newCategoryTitle = '';
        newCategoryImageUrl = '';
      }

      return {
        ...prev,
        applicableCategories: newCategories,
        featuredProducts: newFeaturedProducts,
        categoryTitle: newCategoryTitle,
        categoryImageUrl: newCategoryImageUrl
      };
    });
  };

  const handleProductToggle = (productId) => {
    setFormData(prev => ({
      ...prev,
      featuredProducts: prev.featuredProducts.includes(productId)
        ? prev.featuredProducts.filter(id => id !== productId)
        : [...prev.featuredProducts, productId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setFieldErrors({});

    try {
      const submitData = {
        ...formData,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        termsAndConditions: formData.termsAndConditions.filter(term => term.trim() !== ''),
        discountValue: formData.discountValue ? parseFloat(formData.discountValue) : undefined,
        priority: parseInt(formData.priority) || 0,
        maxUsage: formData.maxUsage ? parseInt(formData.maxUsage) : undefined,
        promoCode: formData.promoCode.trim().toUpperCase() || undefined
      };

      if (promotion) {
        await adminPromotionAPI.update(promotion._id, submitData);
      } else {
        await adminPromotionAPI.create(submitData);
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (err) {
      console.error('Error saving promotion:', err);

      // Handle validation errors
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        const errors = {};
        err.response.data.errors.forEach(error => {
          // Handle both formats: error.field and error.path
          const fieldName = error.field || error.path;
          const errorMessage = error.message || error.msg;
          if (fieldName) {
            errors[fieldName] = errorMessage;
          }
        });
        setFieldErrors(errors);
        setError(err.response?.data?.message || 'Please fix the errors below');
      } else {
        setError(err.response?.data?.message || 'Failed to save promotion');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content large-modal">
        <div className="modal-header">
          <h2>{promotion ? "Edit Promotion" : "Create New Promotion"}</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-grid">
            {error && (
              <div className="error-message" style={{ gridColumn: '1 / -1', margin: 0, marginBottom: '1rem' }}>
                {error}
              </div>
            )}
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Enter promotion title"
                className={fieldErrors.title ? 'error' : ''}
              />
              {fieldErrors.title && (
                <small className="error-text">{fieldErrors.title}</small>
              )}
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="Inactive">Inactive</option>
                <option value="Active">Active</option>
                <option value="Scheduled">Scheduled</option>
                <option value="Expired">Expired</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows="4"
                placeholder="Enter promotion description"
                className={fieldErrors.description ? 'error' : ''}
              />
              {fieldErrors.description && (
                <small className="error-text">{fieldErrors.description}</small>
              )}
            </div>

            <div className="form-group">
              <label>Banner Image URL</label>
              <input
                type="url"
                name="bannerImage"
                value={formData.bannerImage}
                onChange={handleChange}
                placeholder="https://example.com/banner.jpg"
                className={fieldErrors.bannerImage ? 'error' : ''}
              />
              {fieldErrors.bannerImage && (
                <small className="error-text">{fieldErrors.bannerImage}</small>
              )}
            </div>

            <div className="form-group">
              <label>Category Title</label>
              <input
                type="text"
                name="categoryTitle"
                value={formData.categoryTitle}
                readOnly
                placeholder="Auto-populated from selected categories"
                style={{ backgroundColor: '#f7fafc', cursor: 'not-allowed' }}
                title="This field is automatically populated when you select applicable categories below"
              />
              <small style={{ display: 'block', marginTop: '4px', color: '#718096', fontSize: '12px' }}>
                Auto-populated from the first selected category
              </small>
            </div>

            <div className="form-group">
              <label>Category Image URL</label>
              <input
                type="url"
                name="categoryImageUrl"
                value={formData.categoryImageUrl}
                readOnly
                placeholder="Auto-populated from selected categories"
                style={{ backgroundColor: '#f7fafc', cursor: 'not-allowed' }}
                title="This field is automatically populated when you select applicable categories below"
              />
              <small style={{ display: 'block', marginTop: '4px', color: '#718096', fontSize: '12px' }}>
                Auto-populated from the first selected category
              </small>
            </div>

            <div className="form-group">
              <label>Promo Code</label>
              <input
                type="text"
                name="promoCode"
                value={formData.promoCode}
                onChange={handleChange}
                placeholder="SUMMER2024"
                style={{ textTransform: "uppercase" }}
                className={fieldErrors.promoCode ? 'error' : ''}
              />
              {fieldErrors.promoCode && (
                <small className="error-text">{fieldErrors.promoCode}</small>
              )}
            </div>

            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="datetime-local"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                className={fieldErrors.startDate ? 'error' : ''}
              />
              {fieldErrors.startDate && (
                <small className="error-text">{fieldErrors.startDate}</small>
              )}
            </div>

            <div className="form-group">
              <label>End Date *</label>
              <input
                type="datetime-local"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
                className={fieldErrors.endDate ? 'error' : ''}
              />
              {fieldErrors.endDate && (
                <small className="error-text">{fieldErrors.endDate}</small>
              )}
            </div>

            <div className="form-group">
              <label>Discount Type</label>
              <select
                name="discountType"
                value={formData.discountType}
                onChange={handleChange}
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
                <option value="bogo">Buy One Get One</option>
              </select>
            </div>

            <div className="form-group">
              <label>Discount Value</label>
              <input
                type="number"
                name="discountValue"
                value={formData.discountValue}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="10"
                className={fieldErrors.discountValue ? 'error' : ''}
              />
              {fieldErrors.discountValue && (
                <small className="error-text">{fieldErrors.discountValue}</small>
              )}
            </div>

            <div className="form-group">
              <label>Priority</label>
              <input
                type="number"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                min="0"
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label>Max Usage</label>
              <input
                type="number"
                name="maxUsage"
                value={formData.maxUsage}
                onChange={handleChange}
                min="1"
                placeholder="Unlimited"
              />
            </div>

            <div className="form-group full-width">
              <label>Terms & Conditions</label>
              {formData.termsAndConditions.map((term, index) => (
                <div key={index} className="term-input-group">
                  <input
                    type="text"
                    value={term}
                    onChange={(e) => handleTermsChange(index, e.target.value)}
                    placeholder={`Term ${index + 1}`}
                  />
                  {formData.termsAndConditions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTerm(index)}
                      className="btn-remove-term"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addTerm} className="btn-add-term">
                + Add Term
              </button>
            </div>

            <div className="form-group full-width">
              <label>Applicable Categories</label>
              <small style={{ display: 'block', marginBottom: '8px', color: '#718096' }}>
                Select categories to filter featured products
              </small>
              <div className="checkbox-grid">
                {Array.isArray(categories) && categories.length > 0 ? (
                  categories.map((category) => (
                    <label key={category._id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.applicableCategories.some(
                          cat => cat.id === category._id
                        )}
                        onChange={() => handleCategoryToggle(category._id)}
                      />
                      {category.title}
                    </label>
                  ))
                ) : (
                  <p style={{ padding: "1rem", color: "#718096" }}>
                    No categories available
                  </p>
                )}
              </div>
            </div>

            {formData.applicableCategories.length > 0 && (
              <div className="form-group full-width">
                <label>Featured Products</label>
                <small style={{ display: 'block', marginBottom: '8px', color: '#718096' }}>
                  Showing products from selected categories
                </small>
                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="text"
                    placeholder="Search products by name or catalog number..."
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div className="checkbox-grid">
                  {Array.isArray(filteredProducts) && filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => (
                      <label key={product._id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.featuredProducts.includes(
                            product._id
                          )}
                          onChange={() => handleProductToggle(product._id)}
                        />
                        {product.productName} {product.catalogNumber && `(${product.catalogNumber})`}
                      </label>
                    ))
                  ) : (
                    <p style={{ padding: "1rem", color: "#718096" }}>
                      {productSearchQuery.trim()
                        ? `No products found matching "${productSearchQuery}"`
                        : "No products available in selected categories"}
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="form-group full-width">
              <label>SEO Title <small style={{ fontWeight: 400, color: '#718096' }}>(optional, max 70 chars)</small></label>
              <input
                type="text"
                name="seoTitle"
                value={formData.seoTitle}
                onChange={handleChange}
                maxLength={70}
                placeholder="Leave blank to use promotion title"
              />
            </div>

            <div className="form-group full-width">
              <label>SEO Description <small style={{ fontWeight: 400, color: '#718096' }}>(optional, max 200 chars)</small></label>
              <textarea
                name="seoDescription"
                value={formData.seoDescription}
                onChange={handleChange}
                rows="2"
                maxLength={200}
                placeholder="Leave blank to use promotion description"
              />
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading
                ? "Saving..."
                : promotion
                ? "Update Promotion"
                : "Create Promotion"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default PromotionModal;
